import React, { useRef, useState } from "react";
import { paper, serif, sans } from "./docTheme.js";
import SectionBlock, { SpHint } from "./SectionBlock.jsx";
import { hasSharePointLink } from "./publicView.js";
import { DRAFT_TARGETS, findTarget } from "./applyDraft.js";
import CrmSection from "./CrmSection.jsx";

const fmt = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" }) : "");
const btn = { padding: "6px 12px", borderRadius: 6, border: `1px solid ${paper.line}`, background: paper.card, color: paper.inkSoft, fontFamily: sans, fontSize: 13, cursor: "pointer" };

// The meeting document in the "Executive brief" layout (spec §3.2, mockup D). Presenter (phase 3)
// and email (phase 6) render the same data; this is the editable in-tab version.
export default function MeetingDocument({ meeting, clientName, users, loeRows, tiles, editable, onSaveSection, onAddSection, onRemoveSection, onMoveSection, onSaveBlock, onToggleAgenda, onRegenerate, hiddenObjectiveCount: hiddenObjectiveCountProp, crm = null }) {
  const [headline, setHeadline] = useState(null); // string while editing
  const [newTitle, setNewTitle] = useState("");
  const [blockError, setBlockError] = useState(null);
  const [adding, setAdding] = useState(false);
  const typeLabel = meeting.type === "kickoff" ? "Kickoff meeting" : `Bi-weekly update · Meeting ${meeting.number}`;

  // On a block 409 MeetingView adopts the server's block, so the user can simply try again.
  const conflictMessage = (r) => (r.conflict ? `${r.current?.updatedByName || "Someone"} changed this — updated to the latest; try again.` : r.error);

  // One block save at a time: a double click must not race itself into a false conflict.
  const [blockBusy, setBlockBusy] = useState(false);
  const busyRef = useRef(false);
  const saveBlock = async (block, rev, fields) => {
    if (busyRef.current) return null;
    busyRef.current = true; setBlockBusy(true);
    try {
      const r = await onSaveBlock(block, rev, fields);
      setBlockError(r.ok ? null : conflictMessage(r));
      return r;
    } finally { busyRef.current = false; setBlockBusy(false); }
  };

  const saveHeadline = async () => {
    const r = await saveBlock("summary", meeting.summary.rev, { headline, hiddenTiles: meeting.summary.hiddenTiles });
    if (r?.ok) { setHeadline(null); return; } // on failure the typed headline stays in the input
    if (r?.conflict) {
      const theirs = r.current?.headline || "(blank)";
      setBlockError(`${r.current?.updatedByName || "Someone"} changed the headline to “${theirs}”. Your text is still in the box — Save again to replace it, or Cancel to keep theirs.`);
    }
  };
  const toggleTile = (key) => {
    const hidden = meeting.summary.hiddenTiles.includes(key) ? meeting.summary.hiddenTiles.filter((k) => k !== key) : [...meeting.summary.hiddenTiles, key];
    return saveBlock("summary", meeting.summary.rev, { headline: meeting.summary.headline, hiddenTiles: hidden });
  };
  const showAllTiles = () => saveBlock("summary", meeting.summary.rev, { headline: meeting.summary.headline, hiddenTiles: [] });
  const showAllObjectives = () => saveBlock("loe", meeting.loe.rev, { hiddenObjectiveIds: [] });
  const hiddenTileCount = meeting.summary.hiddenTiles.length;
  const hiddenObjectiveCount = hiddenObjectiveCountProp ?? meeting.loe.hiddenObjectiveIds.length;
  const restore = (label, count, ariaLabel, onClick) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: paper.muted }}>
      <span>{label}: {count} ·</span>
      <button type="button" style={{ ...btn, padding: "2px 8px", fontSize: 12 }} onClick={onClick} disabled={blockBusy} aria-label={ariaLabel}>Show all</button>
    </div>
  );
  const hideObjective = (id) => saveBlock("loe", meeting.loe.rev, { hiddenObjectiveIds: [...meeting.loe.hiddenObjectiveIds, id] });
  const clearReview = (section, itemId) =>
    onSaveSection(section.id, section.rev, { title: section.title, items: section.items.map((i) => (i.id === itemId ? { ...i, needsReview: false } : i)) });

  return (
    <div style={{ background: paper.bg, color: paper.ink, fontFamily: sans, borderRadius: 12, border: `1px solid ${paper.line}`, padding: "48px 64px" }}>
      <header style={{ display: "flex", flexDirection: "column", gap: 20, paddingBottom: 36, borderBottom: `1px solid ${paper.line}` }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1.4, textTransform: "uppercase", color: paper.accent }}>{typeLabel}</span>
          <span style={{ fontSize: 15, color: paper.muted }}>
            {clientName} &amp; Sawtooth Advisors · {meeting.type === "kickoff" ? fmt(meeting.meetingDate) : `${fmt(meeting.periodStart)} – ${fmt(meeting.periodEnd)}`}
          </span>
        </div>
        {headline != null ? (
          <div style={{ display: "flex", gap: 8 }}>
            <input aria-label="Headline" maxLength={300} value={headline} onChange={(e) => setHeadline(e.target.value)} style={{ flex: 1, fontFamily: serif, fontSize: 28, padding: "6px 10px", border: `1px solid ${paper.line}`, borderRadius: 6 }} />
            <button type="button" style={btn} onClick={() => { setHeadline(null); setBlockError(null); }}>Cancel</button>
            <button type="button" style={{ ...btn, background: paper.accent, color: "#fff", borderColor: paper.accent }} onClick={saveHeadline} disabled={blockBusy}>Save</button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <h1 style={{ margin: 0, fontFamily: serif, fontWeight: 400, fontSize: 52, lineHeight: 1.08, letterSpacing: -1, flex: 1, color: meeting.summary.headline ? paper.ink : paper.muted }}>
              {meeting.summary.headline || (editable ? "Add a one-line headline for this meeting" : "")}
            </h1>
            {editable && <button type="button" style={btn} onClick={() => setHeadline(meeting.summary.headline)}>Edit headline</button>}
          </div>
        )}
        {editable && headline == null && hasSharePointLink(meeting.summary.headline) && <SpHint style={{ marginTop: -12 }} />}
        {blockError && <div role="alert" style={{ color: paper.danger, fontSize: 14 }}>{blockError}</div>}
        {meeting.type !== "kickoff" && tiles.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${tiles.length}, minmax(0, 1fr))`, gap: 16 }}>
            {tiles.map((t) => (
              <div key={t.key} style={{ background: t.warn ? paper.warnBg : paper.card, border: `1px solid ${t.warn ? paper.warnLine : paper.line}`, borderRadius: 12, padding: 20, display: "flex", flexDirection: "column", gap: 4, position: "relative" }}>
                <span style={{ fontFamily: serif, fontSize: 40, fontWeight: 600, color: t.warn ? paper.warnInk : paper.ink }}>{t.value}</span>
                <span style={{ fontSize: 13, color: t.warn ? paper.warnInk : paper.muted }}>{t.label}</span>
                {editable && <button type="button" onClick={() => toggleTile(t.key)} disabled={blockBusy} aria-label={`Hide tile ${t.label}`} style={{ ...btn, position: "absolute", top: 8, right: 8, padding: "0 6px", fontSize: 12 }}>✕</button>}
              </div>
            ))}
          </div>
        )}
        {editable && meeting.type !== "kickoff" && hiddenTileCount > 0 && restore("Hidden tiles", hiddenTileCount, "Show all tiles", showAllTiles)}
      </header>

      {(loeRows.length > 0 || (editable && hiddenObjectiveCount > 0)) && (
        <section style={{ padding: "40px 0", borderBottom: `1px solid ${paper.line}`, display: "flex", flexDirection: "column", gap: 16 }}>
          <h2 style={{ margin: 0, fontFamily: serif, fontWeight: 600, fontSize: 30 }}>Lines of effort</h2>
          {editable && hiddenObjectiveCount > 0 && restore("Hidden lines of effort", hiddenObjectiveCount, "Show all lines of effort", showAllObjectives)}
          {loeRows.map((r) => (
            <div key={r.id} style={{ background: paper.card, border: `1px solid ${paper.line}`, borderRadius: 14, padding: "22px 28px", display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <span style={{ fontSize: 19, fontWeight: 700, flex: 1 }}>{r.name}</span>
                <span style={{ fontSize: 14, color: paper.muted }}>{r.done} of {r.total} tasks</span>
                <span style={{ fontFamily: serif, fontSize: 26, fontWeight: 600, color: r.pct === 100 ? paper.success : paper.accent }}>{r.pct === 100 ? "Complete" : `${r.pct}%`}</span>
                {editable && <button type="button" style={btn} onClick={() => hideObjective(r.id)} disabled={blockBusy} aria-label={`Hide ${r.name}`}>Hide</button>}
              </div>
              <div style={{ height: 10, background: paper.lineSoft, borderRadius: 5 }}>
                <div style={{ width: `${r.pct}%`, height: 10, background: r.pct === 100 ? paper.success : paper.accent, borderRadius: 5 }} />
              </div>
              {(r.doneInPeriod.length > 0 || r.upNext.length > 0) && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 24, fontSize: 15, lineHeight: 1.55 }}>
                  <div><div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: paper.success }}>Done this period</div>{r.doneInPeriod.join(" · ") || "—"}</div>
                  <div><div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: paper.accent }}>Up next</div>{r.upNext.map((u) => (u.dueDate ? `${u.name} (${u.dueDate})` : u.name)).join(" · ") || "—"}</div>
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      <CrmSection meeting={meeting} crm={crm} editable={editable} busy={blockBusy} btn={btn}
        onSave={(fields) => saveBlock("crm", meeting.crm?.rev ?? 0, fields)} />

      {meeting.sections.map((s, i) => {
        const target = onRegenerate ? DRAFT_TARGETS.find((t) => findTarget(meeting, t)?.id === s.id) : null;
        return (
          <SectionBlock key={s.id} section={s} index={i + 1} editable={editable} users={users}
            onSave={(fields, rev) => onSaveSection(s.id, rev, fields)}
            onRemove={() => onRemoveSection(s.id)}
            onMoveUp={i > 0 ? () => onMoveSection(s.id, -1) : undefined}
            onMoveDown={i < meeting.sections.length - 1 ? () => onMoveSection(s.id, 1) : undefined}
            onToggleAgenda={onToggleAgenda}
            onClearReview={(itemId) => clearReview(s, itemId)}
            onRegenerate={target ? () => onRegenerate(target.key) : undefined} />
        );
      })}

      {editable && (
        <div style={{ display: "flex", gap: 8, paddingTop: 28 }}>
          <input aria-label="New section title" maxLength={120} value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="New section title"
            style={{ flex: 1, padding: "8px 10px", border: `1px solid ${paper.line}`, borderRadius: 6, fontFamily: sans, fontSize: 14 }} />
          <button type="button" style={btn} disabled={!newTitle.trim() || adding} onClick={async () => {
            if (adding) return;
            setAdding(true);
            try {
              const r = await onAddSection(newTitle.trim());
              if (r.ok) { setNewTitle(""); setBlockError(null); }
              else setBlockError(conflictMessage(r));
            } finally { setAdding(false); }
          }}>+ Add section</button>
        </div>
      )}
    </div>
  );
}
